import type { AcademyLine, Course } from '../types/academy'

export const ACADEMY_LINES: { id: AcademyLine; name: string }[] = [
  { id: 'intelligence', name: 'Intelligence' },
  { id: 'growth', name: 'Growth' },
  { id: 'ai', name: 'AI' },
  { id: 'gov', name: 'Gov' },
]

export const PASS_MARK = 70

export const COURSES: Course[] = [
  {
    slug: 'data-driven-decision-making',
    number: 1,
    title: 'Data-Driven Decision Making for Organisations',
    serviceLine: 'intelligence',
    difficulty: 'Beginner',
    hours: 6,
    sectors: [],
    allSectors: true,
    description:
      'How to replace intuition-led decisions with evidence-led ones without building a data team. Covers data sourcing, cleaning, interpretation, and presenting findings to decision makers. Every lesson uses real-world datasets from African and emerging-market contexts.',
    modules: [
      {
        title: 'Why data matters now',
        lessons: ['The cost of bad decisions without data', 'Three types of organisational data', 'Where your data already lives'],
        quizFocus: 'Identifying existing data sources',
        quiz: [
          {
            question: 'A ministry wants to understand clinic attendance. Which of these is most likely an existing data source it already holds?',
            options: [
              { id: 'a', text: 'A new national household survey' },
              { id: 'b', text: 'Clinic registers and monthly facility returns' },
              { id: 'c', text: 'A commissioned satellite imagery study' },
              { id: 'd', text: 'A social media listening tool' },
            ],
            correctId: 'b',
            explanation: 'Routine operational records such as registers and monthly returns are usually the first and cheapest source of evidence.',
          },
          {
            question: 'What is the main cost of a decision made without data?',
            options: [
              { id: 'a', text: 'It always takes longer to make' },
              { id: 'b', text: 'It cannot be approved by a board' },
              { id: 'c', text: 'Errors go unnoticed until they are expensive to reverse' },
              { id: 'd', text: 'It requires more staff to implement' },
            ],
            correctId: 'c',
            explanation: 'Without evidence there is no early signal that a decision is wrong, so mistakes compound before anyone sees them.',
          },
          {
            question: 'Customer complaints held in an email inbox are best described as:',
            options: [
              { id: 'a', text: 'Unstructured data the organisation already holds' },
              { id: 'b', text: 'Not data, because they are not in a spreadsheet' },
              { id: 'c', text: 'External data' },
              { id: 'd', text: 'Data that cannot be analysed' },
            ],
            correctId: 'a',
            explanation: 'Text in emails, documents and forms is unstructured data. It is still evidence and can be categorised and counted.',
          },
        ],
      },
      {
        title: 'Sourcing and cleaning data',
        lessons: [
          'Public datasets (World Bank, UNDP, national statistics offices)',
          'Cleaning messy data in spreadsheets',
          'Data quality checks you can do in 10 minutes',
        ],
        quizFocus: 'Data quality assessment',
        quiz: [
          {
            question: 'A district column contains "Kumasi", "kumasi " and "KUMASI". What is the problem?',
            options: [
              { id: 'a', text: 'Missing values' },
              { id: 'b', text: 'Inconsistent formatting that will split one category into three' },
              { id: 'c', text: 'Duplicate records' },
              { id: 'd', text: 'Outliers' },
            ],
            correctId: 'b',
            explanation: 'Case and trailing spaces make identical values look different, so totals by district will be wrong until they are standardised.',
          },
          {
            question: 'Which is the quickest first check on a new dataset?',
            options: [
              { id: 'a', text: 'Build a dashboard and see if it looks right' },
              { id: 'b', text: 'Count blanks, duplicates and values outside a sensible range per column' },
              { id: 'c', text: 'Run a regression' },
              { id: 'd', text: 'Ask the data owner to certify it' },
            ],
            correctId: 'b',
            explanation: 'Completeness, duplication and range checks take minutes and catch most problems before analysis begins.',
          },
          {
            question: 'When using a World Bank indicator, what should you record alongside the value?',
            options: [
              { id: 'a', text: 'Only the number' },
              { id: 'b', text: 'The source, indicator code, year and date you downloaded it' },
              { id: 'c', text: 'The chart colour you used' },
              { id: 'd', text: 'Nothing, public data is always current' },
            ],
            correctId: 'b',
            explanation: 'Public datasets are revised. Recording source, code, year and access date makes your figures reproducible.',
          },
        ],
      },
      {
        title: 'Analysing data without code',
        lessons: [
          'Pivot tables and cross-tabulation',
          'Spotting trends, outliers, and correlations',
          'Visual analysis with free tools (Google Sheets, Datawrapper)',
        ],
        quizFocus: 'Interpreting a pivot table',
        quiz: [
          {
            question: 'A pivot table shows loan defaults by region and by loan size. This is an example of:',
            options: [
              { id: 'a', text: 'A cross-tabulation' },
              { id: 'b', text: 'A time series' },
              { id: 'c', text: 'A forecast' },
              { id: 'd', text: 'A data dictionary' },
            ],
            correctId: 'a',
            explanation: 'Cross-tabulation summarises one measure across two categories at once.',
          },
          {
            question: 'Two figures rise together over five years. What can you conclude?',
            options: [
              { id: 'a', text: 'One causes the other' },
              { id: 'b', text: 'They are correlated, and the cause still needs to be established' },
              { id: 'c', text: 'The data is wrong' },
              { id: 'd', text: 'Nothing at all' },
            ],
            correctId: 'b',
            explanation: 'Correlation shows the figures move together. A shared driver or coincidence can explain it just as well as causation.',
          },
          {
            question: 'One branch reports ten times the average transactions. What should you do first?',
            options: [
              { id: 'a', text: 'Delete it' },
              { id: 'b', text: 'Check whether it is a data entry error or a genuine outlier' },
              { id: 'c', text: 'Report it as the headline finding' },
              { id: 'd', text: 'Average it with the others' },
            ],
            correctId: 'b',
            explanation: 'Outliers are either errors or the most interesting point in the data. Verify before you remove or highlight it.',
          },
        ],
      },
      {
        title: 'Presenting findings to decision makers',
        lessons: [
          'The one-page brief format',
          'Choosing the right chart for your message',
          'Structuring a data briefing for executives',
        ],
        quizFocus: 'Building a one-page brief from raw data',
        quiz: [
          {
            question: 'What belongs at the top of a one-page brief?',
            options: [
              { id: 'a', text: 'The methodology' },
              { id: 'b', text: 'The decision required and the recommendation' },
              { id: 'c', text: 'A list of data sources' },
              { id: 'd', text: 'The team who prepared it' },
            ],
            correctId: 'b',
            explanation: 'Executives read the top first. Lead with the decision and your recommendation, then the evidence.',
          },
          {
            question: 'Which chart best shows change in one figure over twelve months?',
            options: [
              { id: 'a', text: 'Pie chart' },
              { id: 'b', text: 'Line chart' },
              { id: 'c', text: 'Scatter plot' },
              { id: 'd', text: 'Table of 12 numbers' },
            ],
            correctId: 'b',
            explanation: 'Line charts make direction and rate of change over time easy to read.',
          },
          {
            question: 'A good chart title should:',
            options: [
              { id: 'a', text: 'Name the variables only' },
              { id: 'b', text: 'State the finding the reader should take away' },
              { id: 'c', text: 'Be left blank' },
              { id: 'd', text: 'Repeat the axis labels' },
            ],
            correctId: 'b',
            explanation: 'A title such as "Defaults doubled in the north since March" does the reader’s interpretation for them.',
          },
        ],
      },
      {
        title: 'Building a data culture',
        lessons: ['Getting buy-in from leadership', 'Low-cost data routines for small teams', 'Assigning data ownership'],
        quizFocus: 'Designing a data routine for your team',
        quiz: [
          {
            question: 'What does a data owner do?',
            options: [
              { id: 'a', text: 'Writes all the code' },
              { id: 'b', text: 'Is accountable for a dataset’s definitions, quality and access' },
              { id: 'c', text: 'Buys the software licences' },
              { id: 'd', text: 'Enters every record personally' },
            ],
            correctId: 'b',
            explanation: 'Ownership is accountability, not data entry. The owner answers for whether the data is fit for use.',
          },
          {
            question: 'Which is a sustainable low-cost data routine for a small team?',
            options: [
              { id: 'a', text: 'A quarterly external audit' },
              { id: 'b', text: 'A fixed weekly 30 minute review of three agreed numbers' },
              { id: 'c', text: 'A new dashboard every month' },
              { id: 'd', text: 'Collecting every possible metric' },
            ],
            correctId: 'b',
            explanation: 'Small, regular and focused routines last. A handful of agreed numbers reviewed on a fixed day builds the habit.',
          },
          {
            question: 'The most effective way to win leadership buy-in is to:',
            options: [
              { id: 'a', text: 'Present the technology roadmap' },
              { id: 'b', text: 'Show how data answered a question leadership already cares about' },
              { id: 'c', text: 'Ask for a large budget upfront' },
              { id: 'd', text: 'Share international best practice' },
            ],
            correctId: 'b',
            explanation: 'One concrete answer to a live leadership question is more persuasive than any general case for data.',
          },
        ],
      },
    ],
  },
  {
    slug: 'market-evidence-competitive-intelligence',
    number: 2,
    title: 'Market Evidence and Competitive Intelligence',
    serviceLine: 'intelligence',
    difficulty: 'Intermediate',
    hours: 8,
    sectors: ['SMEs', 'Banking', 'Telecoms', 'Health'],
    description:
      'How to gather, structure, and act on market intelligence using the Cavalla Intelligence method. Designed for strategy teams, business development leads, and consultants who need to size markets, profile competitors, and identify entry points.',
    modules: [
      {
        title: 'Framing the intelligence question',
        lessons: [
          'From business question to research question',
          'Defining scope, geography, and time horizon',
          'The Cavalla evidence capture framework',
        ],
        quizFocus: 'Translating a business question into a research scope',
      },
      {
        title: 'Primary and secondary research methods',
        lessons: [
          'Desk research: databases, filings, reports',
          'Stakeholder interviews: design, execution, synthesis',
          'Survey design for market sizing',
        ],
        quizFocus: 'Choosing the right method for a given question',
      },
      {
        title: 'Market sizing and segmentation',
        lessons: ['Top-down vs bottom-up sizing', 'TAM, SAM, SOM in emerging markets', 'Segmenting by need rather than demographics'],
        quizFocus: 'Calculating a bottom-up market size',
      },
      {
        title: 'Competitive landscape mapping',
        lessons: ['Identifying direct and indirect competitors', 'Building a competitive matrix', 'Pricing and positioning analysis'],
        quizFocus: 'Reading and building a competitive matrix',
      },
      {
        title: 'Synthesising findings into strategy',
        lessons: [
          'The insight pyramid: data to insight to action',
          'Writing an intelligence brief',
          'Updating intelligence as markets move',
        ],
        quizFocus: 'Writing an actionable insight from raw findings',
      },
    ],
  },
  {
    slug: 'research-frameworks-policy-investment',
    number: 3,
    title: 'Research Frameworks for Policy and Investment',
    serviceLine: 'intelligence',
    difficulty: 'Advanced',
    hours: 10,
    sectors: ['Government', 'NGOs', 'Banking'],
    description:
      'How to build the evidence base for a policy decision or an investment thesis. Covers feasibility studies, due diligence research, and impact measurement frameworks used by DFIs, foundations, and government planning units.',
    modules: [
      {
        title: 'The evidence hierarchy for decisions',
        lessons: [
          'Types of evidence: anecdotal, observational, experimental',
          'Matching evidence type to decision stakes',
          'The role of evidence in blended finance and policy',
        ],
        quizFocus: 'Matching evidence type to decision type',
      },
      {
        title: 'Feasibility study design',
        lessons: ['Structuring a feasibility assessment', 'Financial, technical, and social feasibility', 'Country and market risk factors'],
        quizFocus: 'Designing a feasibility study scope',
      },
      {
        title: 'Due diligence research',
        lessons: [
          'Financial due diligence: what to look for',
          'Operational and governance checks',
          'ESG and impact due diligence in emerging markets',
        ],
        quizFocus: 'Identifying red flags in a due diligence review',
      },
      {
        title: 'Impact measurement frameworks',
        lessons: [
          'Theory of change vs logframe vs results framework',
          'Selecting indicators: output, outcome, impact',
          'Data collection plans and baseline studies',
        ],
        quizFocus: 'Building a theory of change for a programme',
      },
      {
        title: 'Presenting evidence to funders and boards',
        lessons: [
          'The investment memo format',
          'Writing for DFIs, foundations, and government committees',
          'Handling uncertainty and assumptions transparently',
        ],
        quizFocus: 'Structuring an investment memo',
      },
    ],
  },
  {
    slug: 'go-to-market-emerging-markets',
    number: 4,
    title: 'Go-to-Market Strategy for African and Emerging Markets',
    serviceLine: 'growth',
    difficulty: 'Intermediate',
    hours: 8,
    sectors: ['SMEs', 'Telecoms', 'Health', 'Agriculture', 'Energy'],
    description:
      'How to design and execute a market entry or growth strategy in Africa and emerging economies. Covers channel selection, pricing for purchasing power, distribution models, and regulatory navigation. Built on Cavalla Growth’s advisory experience.',
    modules: [
      {
        title: 'Market selection and entry timing',
        lessons: [
          'Country screening: macro indicators that matter',
          'Urban vs rural market dynamics',
          'Timing: regulatory windows, infrastructure readiness',
        ],
        quizFocus: 'Scoring countries for market entry',
      },
      {
        title: 'Customer discovery in low-data environments',
        lessons: [
          'Adapting jobs-to-be-done for emerging markets',
          'Field research when secondary data is sparse',
          'Validating demand with pilot programmes',
        ],
        quizFocus: 'Designing a customer discovery sprint',
      },
      {
        title: 'Pricing and revenue models',
        lessons: [
          'Pricing for purchasing power: tiered, freemium, pay-as-you-go',
          'Subscription vs transaction models',
          'Currency risk and payment infrastructure',
        ],
        quizFocus: 'Choosing a pricing model for a given context',
      },
      {
        title: 'Distribution and channel strategy',
        lessons: [
          'Direct sales vs agent networks vs digital channels',
          'Building distribution in low-connectivity areas',
          'Partnership models: telcos, banks, government agencies',
        ],
        quizFocus: 'Mapping a distribution strategy',
      },
      {
        title: 'Regulatory and stakeholder navigation',
        lessons: ['Licensing and compliance by sector', 'Government relations and procurement cycles', 'Building a stakeholder map'],
        quizFocus: 'Building a stakeholder engagement plan',
      },
      {
        title: 'Growth measurement and iteration',
        lessons: [
          'North star metrics for emerging-market products',
          'Cohort analysis with imperfect data',
          'When to pivot, expand, or consolidate',
        ],
        quizFocus: 'Selecting north star metrics',
      },
    ],
  },
  {
    slug: 'digital-demand-generation-b2b-b2g',
    number: 5,
    title: 'Digital Demand Generation for B2B and B2G',
    serviceLine: 'growth',
    difficulty: 'Intermediate',
    hours: 7,
    sectors: [],
    allSectors: true,
    description:
      'How to generate qualified demand for products and services sold to businesses and governments. Covers content strategy, LinkedIn as a B2B engine, event-led growth, and measuring pipeline from marketing activity.',
    modules: [
      {
        title: 'B2B and B2G buyer journeys',
        lessons: [
          'How organisations buy: committees, procurement, RFPs',
          'Mapping the decision unit',
          'Content for each stage of the journey',
        ],
        quizFocus: 'Mapping a B2G buying journey',
      },
      {
        title: 'Content strategy for credibility',
        lessons: [
          'Thought leadership vs product marketing',
          'The research-led content model',
          'Repurposing one piece across five formats',
        ],
        quizFocus: 'Building a content calendar from one research piece',
      },
      {
        title: 'LinkedIn as a B2B growth engine',
        lessons: [
          'Company page vs personal brand strategy',
          'Content formats that perform: carousels, articles, video',
          'LinkedIn advertising: targeting by job title, industry, seniority',
        ],
        quizFocus: 'Writing a LinkedIn content plan',
      },
      {
        title: 'Event-led and partnership-led growth',
        lessons: [
          'Webinars, roundtables, and conference strategy',
          'Co-marketing with complementary organisations',
          'Measuring event ROI beyond attendance',
        ],
        quizFocus: 'Designing a webinar funnel',
      },
      {
        title: 'Pipeline measurement',
        lessons: [
          'Marketing qualified leads vs sales qualified leads',
          'Attribution models for long B2B cycles',
          'Building a dashboard in a spreadsheet',
        ],
        quizFocus: 'Building a pipeline attribution model',
      },
    ],
  },
  {
    slug: 'ai-readiness-five-dimensions',
    number: 6,
    title: 'AI Readiness Assessment: The Five Dimensions',
    serviceLine: 'ai',
    difficulty: 'Beginner',
    hours: 8,
    sectors: [],
    allSectors: true,
    flagship: true,
    description:
      'The flagship Cavalla AI course. Teaches the Five Dimension AI Readiness Framework (Connectivity, Compute, Context, Competency, Governance) and how to assess an organisation’s readiness to adopt AI tools. Learners complete a self-assessment of their own organisation by the end.',
    modules: [
      {
        title: 'What AI readiness means',
        lessons: [
          'AI adoption beyond the hype: what works and what fails',
          'The Five Dimensions overview',
          'Why readiness comes before tools',
        ],
        quizFocus: 'Distinguishing ready vs not-ready organisations',
        quiz: [
          {
            question: 'An organisation scores 4 on Compute and 1 on Governance. What is its starting point?',
            options: [
              { id: 'a', text: 'Its average, 2.5' },
              { id: 'b', text: 'Governance, because the lowest dimension sets the starting point' },
              { id: 'c', text: 'Compute, because it is strongest' },
              { id: 'd', text: 'It can deploy immediately' },
            ],
            correctId: 'b',
            explanation: 'The lowest score, not the average, decides where to start. A governance gap undermines whatever the compute enables.',
          },
          {
            question: 'Which of these is NOT one of the Five Dimensions?',
            options: [
              { id: 'a', text: 'Context' },
              { id: 'b', text: 'Competency' },
              { id: 'c', text: 'Capital' },
              { id: 'd', text: 'Connectivity' },
            ],
            correctId: 'c',
            explanation: 'The five are Connectivity, Compute, Context, Competency and Governance.',
          },
          {
            question: 'Why does readiness come before choosing tools?',
            options: [
              { id: 'a', text: 'Tools are always too expensive' },
              { id: 'b', text: 'The technology can be bought quickly, but the conditions that make it useful take longer to build' },
              { id: 'c', text: 'Vendors require a readiness certificate' },
              { id: 'd', text: 'It does not; tools should come first' },
            ],
            correctId: 'b',
            explanation: 'Readiness is an operating condition, not a procurement question.',
          },
        ],
      },
      {
        title: 'Connectivity',
        lessons: [
          'Internet infrastructure and bandwidth requirements',
          'API and system integration readiness',
          'Data flow between departments',
        ],
        quizFocus: 'Assessing connectivity gaps',
        quiz: [
          {
            question: 'What is the most useful connectivity test for an organisation?',
            options: [
              { id: 'a', text: 'National internet penetration' },
              { id: 'b', text: 'Whether the people and systems in the target workflow can reach the AI service consistently' },
              { id: 'c', text: 'Head office bandwidth only' },
              { id: 'd', text: 'Number of laptops issued' },
            ],
            correctId: 'b',
            explanation: 'Connectivity is judged at the workflow: if access changes by location, device or time of day, the foundation is not stable.',
          },
          {
            question: 'Two departments exchange data by emailing spreadsheets monthly. This is a gap in:',
            options: [
              { id: 'a', text: 'Data flow and integration readiness' },
              { id: 'b', text: 'Compute' },
              { id: 'c', text: 'Governance only' },
              { id: 'd', text: 'Nothing, this is fine' },
            ],
            correctId: 'a',
            explanation: 'Manual, infrequent transfers mean an AI system downstream will work from stale or inconsistent data.',
          },
          {
            question: 'An API is best described as:',
            options: [
              { id: 'a', text: 'A type of AI model' },
              { id: 'b', text: 'A defined way for two systems to exchange data automatically' },
              { id: 'c', text: 'A network cable standard' },
              { id: 'd', text: 'A data protection law' },
            ],
            correctId: 'b',
            explanation: 'APIs let systems request and send data to each other without manual handling.',
          },
        ],
      },
      {
        title: 'Compute',
        lessons: [
          'Hardware and cloud requirements for AI workloads',
          'Cost models: on-premise vs cloud vs hybrid',
          'Right-sizing compute for your use case',
        ],
        quizFocus: 'Estimating compute needs for a use case',
        quiz: [
          {
            question: 'Most organisations starting with AI should:',
            options: [
              { id: 'a', text: 'Build their own data centre' },
              { id: 'b', text: 'Decide where computing happens, what data leaves, required latency and how cost scales' },
              { id: 'c', text: 'Buy the largest GPU available' },
              { id: 'd', text: 'Avoid cloud entirely' },
            ],
            correctId: 'b',
            explanation: 'The compute question is a set of decisions about location, data, latency and cost, not a hardware purchase.',
          },
          {
            question: 'A hybrid compute model typically means:',
            options: [
              { id: 'a', text: 'Using two cloud providers for the same task' },
              { id: 'b', text: 'Keeping sensitive processing on premise while using cloud for the rest' },
              { id: 'c', text: 'Running AI on mobile phones only' },
              { id: 'd', text: 'Renting hardware monthly' },
            ],
            correctId: 'b',
            explanation: 'Hybrid splits workloads by sensitivity, cost or latency between on-premise and cloud.',
          },
          {
            question: 'What usually drives AI compute cost over time?',
            options: [
              { id: 'a', text: 'The initial build' },
              { id: 'b', text: 'Usage volume once the system is in daily operation' },
              { id: 'c', text: 'The number of developers' },
              { id: 'd', text: 'Office electricity' },
            ],
            correctId: 'b',
            explanation: 'Running cost scales with usage, which is why a two year cost model matters more than the build quote.',
          },
        ],
      },
      {
        title: 'Context',
        lessons: [
          'Data availability and quality for AI training',
          'Domain knowledge as a competitive advantage',
          'Building contextual datasets from existing operations',
        ],
        quizFocus: 'Evaluating data readiness for an AI project',
        quiz: [
          {
            question: 'A capable model gives weak answers about your products. The most likely cause is missing:',
            options: [
              { id: 'a', text: 'Compute' },
              { id: 'b', text: 'Context: the organisation’s definitions, records and terminology' },
              { id: 'c', text: 'Connectivity' },
              { id: 'd', text: 'A bigger model' },
            ],
            correctId: 'b',
            explanation: 'Context is the information that defines the organisation’s reality. Without it a model produces generic answers.',
          },
          {
            question: 'Which is the best first step to build a contextual dataset?',
            options: [
              { id: 'a', text: 'Buy a third-party dataset' },
              { id: 'b', text: 'Agree definitions for the fields your chosen use case depends on' },
              { id: 'c', text: 'Collect every document in the organisation' },
              { id: 'd', text: 'Train a model from scratch' },
            ],
            correctId: 'b',
            explanation: 'Agreed definitions prevent the model inheriting disputes between departments.',
          },
          {
            question: 'Why is domain knowledge an advantage in AI projects?',
            options: [
              { id: 'a', text: 'It replaces the need for data' },
              { id: 'b', text: 'It is hard for competitors to copy and lets you judge whether outputs make sense' },
              { id: 'c', text: 'Vendors charge less for it' },
              { id: 'd', text: 'It speeds up compute' },
            ],
            correctId: 'b',
            explanation: 'Local, sector-specific knowledge both improves the system and lets people catch its mistakes.',
          },
        ],
      },
      {
        title: 'Competency',
        lessons: [
          'Skills audit: what your team can and cannot do',
          'Build vs hire vs outsource decisions',
          'Upskilling pathways for non-technical staff',
        ],
        quizFocus: 'Designing a skills development plan',
        quiz: [
          {
            question: 'Which competency is most often missing in AI programmes?',
            options: [
              { id: 'a', text: 'Users who can judge whether an output is correct' },
              { id: 'b', text: 'Typing speed' },
              { id: 'c', text: 'Presentation skills' },
              { id: 'd', text: 'Procurement knowledge' },
            ],
            correctId: 'a',
            explanation: 'AI changes the skills around the workflow. An accountable person must still decide whether an answer is right.',
          },
          {
            question: 'When does outsourcing a capability make most sense?',
            options: [
              { id: 'a', text: 'When it is core to your long-term advantage' },
              { id: 'b', text: 'When it is specialist, needed occasionally, and not core to how you compete' },
              { id: 'c', text: 'Always' },
              { id: 'd', text: 'Never' },
            ],
            correctId: 'b',
            explanation: 'Build or hire what is core and recurring; outsource what is specialist and occasional.',
          },
          {
            question: 'A good skills audit starts with:',
            options: [
              { id: 'a', text: 'A list of AI certifications to buy' },
              { id: 'b', text: 'The tasks in the target workflow and who performs them today' },
              { id: 'c', text: 'An organisation-wide survey on AI attitudes' },
              { id: 'd', text: 'Hiring a data scientist' },
            ],
            correctId: 'b',
            explanation: 'Map the workflow first; the skills gap is the difference between what it needs and what the people in it can do.',
          },
        ],
      },
      {
        title: 'Governance',
        lessons: [
          'AI ethics and bias in organisational context',
          'Policy frameworks: EU AI Act Article 50, AU data governance',
          'Accountability structures and human oversight',
        ],
        quizFocus: 'Drafting an AI governance checklist',
        quiz: [
          {
            question: 'A model makes recommendations and nobody is named to accept or reject them. The system is:',
            options: [
              { id: 'a', text: 'Governed' },
              { id: 'b', text: 'Running, not governed' },
              { id: 'c', text: 'Compliant by default' },
              { id: 'd', text: 'Ready to scale' },
            ],
            correctId: 'b',
            explanation: 'Every output should reach a named person with authority, context and accountability to act on it.',
          },
          {
            question: 'Which belongs in an AI governance checklist?',
            options: [
              { id: 'a', text: 'Owner, approval thresholds, monitoring, escalation and stop conditions' },
              { id: 'b', text: 'The model’s parameter count' },
              { id: 'c', text: 'The vendor’s marketing claims' },
              { id: 'd', text: 'Office seating plan' },
            ],
            correctId: 'a',
            explanation: 'Governance defines who owns it, what it may decide, how it is watched, how concerns rise and when it stops.',
          },
          {
            question: 'Where does bias in an AI system most commonly come from?',
            options: [
              { id: 'a', text: 'Only from the algorithm' },
              { id: 'b', text: 'Training data, model design and the context it is deployed in' },
              { id: 'c', text: 'The user interface' },
              { id: 'd', text: 'Network latency' },
            ],
            correctId: 'b',
            explanation: 'Bias can enter at every stage, which is why it must be tested in the deployment context, not just in the lab.',
          },
        ],
      },
      {
        title: 'Running the assessment',
        lessons: [
          'The Cavalla readiness scorecard tool',
          'Conducting interviews and workshops',
          'Presenting findings and building a roadmap',
        ],
        quizFocus: 'Completing a full organisational assessment',
        quiz: [
          {
            question: 'What is the correct assessment sequence?',
            options: [
              { id: 'a', text: 'Choose a model, then find a use for it' },
              { id: 'b', text: 'Business decision, data and infrastructure, connectivity and compute, context and competency, then governance' },
              { id: 'c', text: 'Governance first, then everything else in any order' },
              { id: 'd', text: 'Score all five at once and average them' },
            ],
            correctId: 'b',
            explanation: 'Starting from the decision keeps the assessment anchored to value rather than technology.',
          },
          {
            question: 'Each dimension is scored on a 1 to 5 band. What does a 3 (Developing) mean?',
            options: [
              { id: 'a', text: 'Not ready for any AI work' },
              { id: 'b', text: 'Workable for a pilot, not for scale' },
              { id: 'c', text: 'Ready for production' },
              { id: 'd', text: 'Robust and monitored' },
            ],
            correctId: 'b',
            explanation: 'Developing means proceed with a pilot and defined constraints, not a full rollout.',
          },
          {
            question: 'A readiness roadmap should prioritise:',
            options: [
              { id: 'a', text: 'The strongest dimension, to build momentum' },
              { id: 'b', text: 'The lowest-scoring dimension that blocks the chosen use case' },
              { id: 'c', text: 'All five dimensions equally' },
              { id: 'd', text: 'Whatever the vendor recommends' },
            ],
            correctId: 'b',
            explanation: 'Fix the binding constraint first. Investment elsewhere is wasted while it remains.',
          },
        ],
      },
    ],
  },
  {
    slug: 'responsible-ai-governance',
    number: 7,
    title: 'Responsible AI Governance for Organisations',
    serviceLine: 'ai',
    difficulty: 'Advanced',
    hours: 9,
    sectors: ['Government', 'Banking', 'Health', 'NGOs'],
    description:
      'How to build the policies, processes, and oversight structures that make AI adoption safe and accountable. Covers the EU AI Act, African Union data governance frameworks, and practical governance models for organisations that lack dedicated AI teams.',
    modules: [
      {
        title: 'The governance case for AI',
        lessons: [
          'What goes wrong without governance: case studies',
          'Reputational, legal, and operational risks',
          'The cost of not governing vs the cost of governing',
        ],
        quizFocus: 'Identifying governance risks in a scenario',
      },
      {
        title: 'Regulatory landscape',
        lessons: [
          'EU AI Act: risk categories and compliance obligations',
          'African Union Convention on Cyber Security',
          'National data protection laws (GDPR, Nigeria NDPA, Kenya DPA, Rwanda law)',
        ],
        quizFocus: 'Classifying an AI system by EU AI Act risk tier',
      },
      {
        title: 'Building an AI policy',
        lessons: [
          'Policy components: scope, principles, prohibited uses',
          'Acceptable use policies for generative AI',
          'Procurement policies for third-party AI tools',
        ],
        quizFocus: 'Drafting an AI acceptable use policy',
      },
      {
        title: 'Bias detection and mitigation',
        lessons: [
          'Sources of bias: training data, model design, deployment context',
          'Testing for bias in classification and recommendation systems',
          'Remediation strategies and documentation',
        ],
        quizFocus: 'Identifying bias sources in a dataset',
      },
      {
        title: 'Accountability and human oversight',
        lessons: [
          'Decision rights: who can override an AI recommendation',
          'Audit trails and explainability requirements',
          'The role of an AI ethics committee',
        ],
        quizFocus: 'Designing an accountability structure',
      },
      {
        title: 'Implementation and monitoring',
        lessons: [
          'Rolling out governance in phases',
          'Ongoing monitoring: model drift, performance degradation',
          'Annual review and policy update cycles',
        ],
        quizFocus: 'Creating a governance implementation timeline',
      },
    ],
  },
  {
    slug: 'applied-ml-non-technical-leaders',
    number: 8,
    title: 'Applied Machine Learning for Non-Technical Leaders',
    serviceLine: 'ai',
    difficulty: 'Beginner',
    hours: 6,
    sectors: [],
    allSectors: true,
    description:
      'A no-code, no-jargon introduction to what machine learning can and cannot do, designed for executives, programme managers, and policy makers who commission or oversee AI projects. Learners leave able to evaluate an AI vendor pitch, ask the right questions, and spot unrealistic claims.',
    modules: [
      {
        title: 'Machine learning without the maths',
        lessons: [
          'What a model actually does: patterns, predictions, classifications',
          'Supervised vs unsupervised vs reinforcement learning (plain language)',
          'Real examples: fraud detection, crop yield prediction, document classification',
        ],
        quizFocus: 'Identifying the ML type in a use case',
      },
      {
        title: 'When to use AI and when not to',
        lessons: [
          'Decision framework: rules-based vs ML vs human judgement',
          'Data requirements for a viable ML project',
          'The build vs buy decision',
        ],
        quizFocus: 'Applying the decision framework to a scenario',
      },
      {
        title: 'Evaluating AI vendors and tools',
        lessons: [
          'What to ask in an AI vendor evaluation',
          'Red flags: accuracy claims, black-box models, no bias audit',
          'Pilot design: scoping, timeline, success criteria',
        ],
        quizFocus: 'Evaluating an AI vendor pitch',
      },
      {
        title: 'Data preparation for AI projects',
        lessons: [
          'What “clean data” means in practice',
          'Data labelling, annotation, and ground truth',
          'Privacy and consent in training data',
        ],
        quizFocus: 'Assessing a dataset for ML readiness',
      },
      {
        title: 'Managing an AI project',
        lessons: [
          'Project structure: data science vs software engineering',
          'Milestones, deliverables, and success metrics',
          'What to do when the model does not perform',
        ],
        quizFocus: 'Setting milestones for an AI project',
      },
    ],
  },
  {
    slug: 'digital-government-delivery',
    number: 9,
    title: 'Digital Government Delivery: From Plan to Performance',
    serviceLine: 'gov',
    difficulty: 'Intermediate',
    hours: 9,
    sectors: ['Government'],
    description:
      'How to track, manage, and report on national and subnational development plans using digital tools. Built around the Cavalla Gov Delivery Tracker methodology and designed for government planning officers, M&E units, and development partners supporting public sector delivery.',
    modules: [
      {
        title: 'The delivery problem in government',
        lessons: [
          'Why plans fail: capability, coordination, accountability gaps',
          'Delivery units: origins, models, and evidence of impact',
          'The role of real-time data in closing the delivery gap',
        ],
        quizFocus: 'Diagnosing a delivery bottleneck',
      },
      {
        title: 'Structuring a delivery framework',
        lessons: [
          'Breaking a national plan into trackable commitments',
          'Defining milestones, indicators, and responsible owners',
          'Cascading from national to ministry to department level',
        ],
        quizFocus: 'Structuring commitments from a national plan',
      },
      {
        title: 'Data collection and reporting',
        lessons: [
          'Routine data collection: who reports what, how often',
          'Data quality assurance at source',
          'Automated vs manual reporting workflows',
        ],
        quizFocus: 'Designing a reporting workflow',
      },
      {
        title: 'Performance dashboards and stocktakes',
        lessons: [
          'Dashboard design for decision makers (not analysts)',
          'Traffic light systems and exception reporting',
          'Running effective stocktake meetings with live data',
        ],
        quizFocus: 'Interpreting a delivery dashboard',
      },
      {
        title: 'Accountability and course correction',
        lessons: [
          'Escalation protocols when milestones slip',
          'Root cause analysis for underperformance',
          'Mid-term plan revision based on delivery data',
        ],
        quizFocus: 'Designing an escalation protocol',
      },
      {
        title: 'Technology for government delivery',
        lessons: [
          'Digital tools for delivery tracking (including Cavalla Gov Delivery Tracker)',
          'Integration with national M&E systems',
          'Procurement and deployment considerations for government IT',
        ],
        quizFocus: 'Evaluating a delivery tracking tool',
      },
    ],
  },
  {
    slug: 'development-finance-intelligence',
    number: 10,
    title: 'Development Finance Intelligence for Public Officials',
    serviceLine: 'gov',
    difficulty: 'Advanced',
    hours: 8,
    sectors: ['Government', 'NGOs'],
    description:
      'How to find, analyse, and act on development finance data: ODA flows, DFI commitments, climate finance, and domestic resource mobilisation. Designed for finance ministry staff, aid coordination units, and development partners who need to understand where money comes from, where it goes, and whether it delivers.',
    modules: [
      {
        title: 'The development finance landscape',
        lessons: [
          'ODA, DFI lending, climate finance, remittances, domestic revenue: a map of flows',
          'Who gives, who receives, and how the channels work',
          'Trends: declining ODA, rising DFI, the blended finance shift',
        ],
        quizFocus: 'Mapping finance flows for a country',
      },
      {
        title: 'Data sources and platforms',
        lessons: [
          'OECD DAC Creditor Reporting System',
          'IATI and country-level aid management systems',
          'Climate finance trackers (CPI, UNFCCC)',
          'Domestic revenue data (IMF, World Bank GFS)',
        ],
        quizFocus: 'Navigating OECD CRS data',
      },
      {
        title: 'Analysing finance flows',
        lessons: [
          'Disbursement vs commitment data',
          'Sector and geographic allocation analysis',
          'Comparing donor portfolios and identifying gaps',
        ],
        quizFocus: 'Analysing sector allocation from CRS data',
      },
      {
        title: 'Aid coordination and donor engagement',
        lessons: [
          'Building a donor mapping for your country/sector',
          'Preparing for donor roundtables and aid reviews',
          'Data-driven negotiation: using evidence in funding discussions',
        ],
        quizFocus: 'Preparing a donor engagement brief',
      },
      {
        title: 'Domestic resource mobilisation',
        lessons: [
          'Tax-to-GDP benchmarks and what drives them',
          'Illicit financial flows and revenue leakage',
          'Digital tax administration and compliance',
        ],
        quizFocus: 'Analysing domestic revenue potential',
      },
      {
        title: 'Reporting and accountability',
        lessons: [
          'AIMS (Aid Information Management Systems): what they do and what they miss',
          'Publishing to IATI: compliance and practical steps',
          'Building a development finance dashboard for your ministry',
        ],
        quizFocus: 'Designing a ministry finance dashboard',
      },
    ],
  },
]

export function getCourse(slug: string): Course | undefined {
  return COURSES.find((course) => course.slug === slug)
}
